import { useCallback, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import type { Licence } from "@xop/dashboard-shared"

import { newLicencePath } from "@crm-src/pages/licences/create-licence-dialog"
import { LicenceStatusChip, LicenceWorkflowChip } from "@crm-src/pages/licences/licence-chips"
import { fetchLicences } from "@crm-src/pages/licences/licences-api"
import { formatLicenceDate, licenceActorName } from "@crm-src/pages/licences/licence-provenance"
import { ResellerOnly } from "@crm-src/components/reseller"
import usePermissions from "@crm-src/hooks/usePermissions"

/**
 * The customer profile's Licences tab (its own tab since 2026-09-15).
 *
 * EVERY licence for the customer, whoever made it. `?customerId=` is a plain
 * filter over the caller's scope in crm-api, so a licence a RESELLER built and
 * issued for this customer lists here beside XOP's own — and the "Issued by"
 * column says which. The reseller's name sits behind <ResellerOnly> (and crm-api
 * omits provenance behind the reseller wall anyway); without reseller access the
 * cell reads "—" rather than "XOP", which would be a lie for a reseller's file.
 *
 * IT SITS BESIDE THE AGREEMENT, NOT INSTEAD OF IT, and the copy says why. The
 * agreement's ports are what the customer BOUGHT; each licence's ports are what
 * ONE BOX may run. A customer with two appliances splits its purchased capacity
 * between them, so the two figures genuinely differ — and a reader who thinks
 * one contradicts the other will "fix" it.
 */
export function CustomerLicencesPanel({
  customerId,
  customerName,
}: {
  customerId: number
  customerName: string
}) {
  const navigate = useNavigate()
  const { canAccessLicences } = usePermissions()
  const [licences, setLicences] = useState<Licence[]>([])
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setFailed(false)
    try {
      setLicences(await fetchLicences({ customerId }))
    } catch {
      // "Could not load" is a different fact from "holds none" — see below.
      setFailed(true)
    } finally {
      setLoading(false)
    }
  }, [customerId])

  useEffect(() => {
    if (!canAccessLicences()) {
      setLoading(false)
      return
    }
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load])

  if (!canAccessLicences()) return null

  return (
    <Card>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6">Licences</Typography>
          <Stack direction="row" spacing={1}>
            <Button size="small" onClick={() => navigate("/crm/licences")}>
              All licences
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={() => navigate(newLicencePath({ id: customerId, name: customerName }))}
            >
              Add licence
            </Button>
          </Stack>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          What each appliance is licensed to run — including licences a reseller issued for this
          customer. The Agreements tab records what was purchased; a customer with more than one
          box splits that capacity between them.
        </Typography>

        {loading ? (
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        ) : failed ? (
          <Box>
            <Typography variant="body2" color="error" sx={{ mb: 1 }}>
              Could not load licences.
            </Typography>
            <Button size="small" onClick={() => void load()}>
              Try again
            </Button>
          </Box>
        ) : licences.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No licences yet.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Appliance</TableCell>
                  <TableCell>Hardware address</TableCell>
                  <TableCell>Licensed to this appliance</TableCell>
                  <TableCell>Features</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell>Term</TableCell>
                  <TableCell>Workflow</TableCell>
                  <TableCell>Issued by</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {licences.map((licence) => (
                  <TableRow
                    key={licence.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => navigate(`/crm/licences/${licence.id}`)}
                  >
                    <TableCell>{licence.label || "—"}</TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                      {licence.hardwareAddress}
                    </TableCell>
                    <TableCell>
                      {licence.ports.audioConferencing} audio / {licence.ports.webConferencing} web
                    </TableCell>
                    <TableCell>{licence.features.length}</TableCell>
                    <TableCell>{licence.expiresOn ?? "No end date"}</TableCell>
                    <TableCell>
                      <LicenceStatusChip licence={licence} />
                    </TableCell>
                    <TableCell>
                      <LicenceWorkflowChip licence={licence} />
                    </TableCell>
                    <TableCell data-cy="customer-licence-issued-by">
                      <ResellerOnly fallback="—">
                        <IssuedBy licence={licence} />
                      </ResellerOnly>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  )
}

/** "Baltic Comms" (a reseller chip) or "XOP", the serial + date under it, and
 * "Not issued yet" for a licence with no file. Falls back to who CREATED it
 * when the issue predates provenance — the only record there is. */
function IssuedBy({ licence }: { licence: Licence }) {
  const issue = licence.currentIssue
  if (!issue) {
    return (
      <Typography variant="body2" color="text.secondary">
        Not issued yet
      </Typography>
    )
  }
  const provenance = issue.issuedBy ?? licence.createdBy
  const name = licenceActorName(provenance)
  const on = formatLicenceDate(issue.issuedAt)
  return (
    <Stack spacing={0.25} alignItems="flex-start">
      {name ? (
        <Chip
          size="small"
          variant="outlined"
          color={provenance?.kind === "reseller" ? "secondary" : "default"}
          label={provenance?.kind === "reseller" ? `Reseller · ${name}` : name}
        />
      ) : (
        <Typography variant="body2" color="text.secondary">—</Typography>
      )}
      <Typography variant="caption" color="text.secondary">
        {issue.serial}
        {on ? ` · ${on}` : ""}
      </Typography>
    </Stack>
  )
}
